import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { Cable } from '@mui/icons-material';
import { webSerialSupported } from '../flash/esptool';
import { useUsbSession } from '../flash/UsbSessionContext';
import { useI18n } from '../i18n';

interface SerialConsoleProps {
  locked?: boolean;
  connectLabel?: string;
  /** Drop outer Paper/title — for use inside a Dialog. */
  embedded?: boolean;
}

const SerialConsole: React.FC<SerialConsoleProps> = ({
  locked = false,
  connectLabel,
  embedded = false,
}) => {
  const { t } = useI18n();
  const usb = useUsbSession();
  const [command, setCommand] = useState('');
  const logRef = useRef<HTMLPreElement | null>(null);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [usb.log]);

  const handleSend = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!command.trim()) {
      return;
    }
    const line = command;
    setCommand('');
    await usb.sendLine(line);
  };

  const body = (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        {!embedded && <Typography variant="h6">{t('usb.title')}</Typography>}
        <Box flexGrow={embedded ? 1 : 0} />
        {usb.connected ? (
          <Button size="small" onClick={() => usb.disconnect()} disabled={usb.busy || locked}>
            {t('usb.disconnect')}
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            startIcon={<Cable />}
            onClick={() => usb.connect().catch(() => undefined)}
            disabled={usb.busy || locked || !webSerialSupported()}
          >
            {connectLabel || t('usb.connect')}
          </Button>
        )}
      </Box>
      {usb.error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={usb.clearError}>
          {usb.error}
        </Alert>
      )}
      <Box
        ref={logRef}
        component="pre"
        sx={{
          m: 0,
          mb: 1,
          p: 1.5,
          height: embedded ? 280 : 220,
          overflow: 'auto',
          bgcolor: '#111',
          color: '#d4f7d4',
          borderRadius: 1,
          fontSize: 12,
          fontFamily: 'ui-monospace, Consolas, monospace',
          whiteSpace: 'pre-wrap',
        }}
      >
        {usb.log || (usb.connected ? t('usb.waiting') : t('usb.notConnected'))}
      </Box>
      <Box component="form" onSubmit={handleSend} display="flex" gap={1}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('usb.placeholder')}
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          disabled={!usb.connected || usb.busy || locked}
        />
        <Button type="submit" variant="contained" disabled={!usb.connected || usb.busy || locked || !command.trim()}>
          {t('common.send')}
        </Button>
      </Box>
    </>
  );

  if (embedded) {
    return <Box>{body}</Box>;
  }
  return <Paper sx={{ p: 2 }}>{body}</Paper>;
};

export default SerialConsole;
